/**
 * Adaptive telemetry fields: a list of dicts the UI can render without knowing
 * the robot's sensor set in advance.
 *
 * Two input shapes:
 *   1. Already a list (or `{ fields: [...] }`) of `{ key, label?, unit?, type?, value }`
 *   2. An arbitrary nested payload — flattened to dotted keys.
 */

export type TelemetryValueType = "number" | "string" | "boolean";

export interface TelemetryField {
  key: string;
  label: string;
  value: number | string | boolean | null;
  unit?: string;
  type: TelemetryValueType;
}

const MAX_FIELDS = 64;
const MAX_DEPTH = 6;

function inferType(value: unknown): TelemetryValueType {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
}

function coerceValue(
  value: unknown,
  type: TelemetryValueType
): number | string | boolean | null {
  if (value == null) return null;
  if (type === "number") {
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "boolean") {
    if (typeof value === "boolean") return value;
    if (value === "true" || value === 1) return true;
    if (value === "false" || value === 0) return false;
    return null;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function humanize(path: string[]): string {
  const last = path[path.length - 1] ?? path.join(".");
  return last.replace(/_/g, " ");
}

function parseFieldDict(raw: unknown): TelemetryField | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  const key = String(rec.key ?? "").trim();
  if (!key) return null;
  const typeRaw = String(rec.type ?? "").trim();
  const type: TelemetryValueType =
    typeRaw === "number" || typeRaw === "string" || typeRaw === "boolean"
      ? typeRaw
      : inferType(rec.value);
  const unit = rec.unit == null || rec.unit === "" ? undefined : String(rec.unit);
  const label = String(rec.label ?? "").trim() || key.replace(/_/g, " ");
  return {
    key,
    label,
    unit,
    type,
    value: coerceValue(rec.value, type),
  };
}

function flatten(value: unknown, path: string[], out: TelemetryField[]): void {
  if (out.length >= MAX_FIELDS || path.length > MAX_DEPTH) return;
  if (value == null) {
    if (path.length === 0) return;
    out.push({
      key: path.join("."),
      label: humanize(path),
      value: null,
      type: "string",
    });
    return;
  }
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "string") {
    if (path.length === 0) return;
    const type = inferType(value);
    out.push({
      key: path.join("."),
      label: humanize(path),
      value: coerceValue(value, type),
      type,
    });
    return;
  }
  if (Array.isArray(value)) {
    const primitive = value.every((item) => item == null || typeof item !== "object");
    if (primitive) {
      if (path.length === 0) return;
      out.push({
        key: path.join("."),
        label: humanize(path),
        value: value.filter((item) => item != null).map(String).join(", ") || null,
        type: "string",
      });
      return;
    }
    value.forEach((item, i) => flatten(item, [...path, String(i)], out));
    return;
  }
  if (typeof value === "object") {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (!k) continue;
      flatten(v, [...path, k], out);
      if (out.length >= MAX_FIELDS) return;
    }
  }
}

/**
 * Turn a telemetry payload into a list of display fields.
 * Prefers an explicit list of dicts when the robot sends one.
 */
export function normalizeTelemetryFields(payload: unknown): TelemetryField[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) {
    return payload.map(parseFieldDict).filter((f): f is TelemetryField => f != null).slice(0, MAX_FIELDS);
  }
  if (typeof payload === "object") {
    const rec = payload as Record<string, unknown>;
    if (Array.isArray(rec.fields)) {
      return rec.fields
        .map(parseFieldDict)
        .filter((f): f is TelemetryField => f != null)
        .slice(0, MAX_FIELDS);
    }
  }
  const out: TelemetryField[] = [];
  flatten(payload, [], out);
  return out;
}
